import ks, { kObject, kObjectArray } from "@shared/kalmstromShared4";
import { kEditableTable, kHTMLEditableColType } from "@shared/kEditableTable";
import { IListDefinition, IFieldDefinition } from "./DBInterfaces";
import { FIELD_TYPES } from "./DBConstants";
import DBState from "./DBState";
import DBModalManager from "./DBModalManager";
import DBListManager from "./DBListManager";

// ---------------------------------------------------------------------------
// Data model
// ---------------------------------------------------------------------------

class FieldRow extends kObject {
    public InternalName: string = "";
    public Title: string = "";
    public TypeLabel: string = "";
    public Required: boolean = false;
    public MaxLength: number = 0;
    public DefaultValue: string = "";

    constructor() { super(); }
}

class FieldRowCollection extends kObjectArray {
    public Items: FieldRow[] = [];
}

// ---------------------------------------------------------------------------

export default class DBFieldEditor {

    private static FieldEditorGrid: kEditableTable | null = null;

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    public static OpenFieldEditor = (listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }
            DBState.FieldEditorListId = listId;

            const titleEl: HTMLElement | null = document.getElementById("fieldEditorModalTitle");
            if (titleEl !== null) { titleEl.textContent = "Field Editor: " + list.Title; }

            DBFieldEditor.BuildGrid(list);
            DBModalManager.OpenModal("fieldEditorModal");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldEditor.OpenFieldEditor");
        }
    };

    public static SaveFieldEditor = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(DBState.FieldEditorListId || "");
            if (list === null) { return; }
            if (DBFieldEditor.FieldEditorGrid === null) { return; }

            let updatedCount: number = 0;
            for (const gridRow of DBFieldEditor.FieldEditorGrid.Rows.Items) {
                const internalName: string = String(gridRow.GetValueFromCell("InternalName") || "");
                if (!internalName || !(internalName in list.Properties)) { continue; }
                const field: IFieldDefinition = list.Properties[internalName];
                field.Title = String(gridRow.GetValueFromCell("Title") || field.Title);
                field.Required = gridRow.GetValueFromCell("Required") === true;
                const maxLen: any = gridRow.GetValueFromCell("MaxLength");
                field.MaxLength = typeof maxLen === "number" ? maxLen : (parseInt(String(maxLen)) || 0);
                field.DefaultValue = String(gridRow.GetValueFromCell("DefaultValue") || "");
                updatedCount++;
            }

            DBState.SaveState();
            DBListManager.RenderLists();
            DBModalManager.CloseModal("fieldEditorModal");
            DBModalManager.ShowToast("Fields saved: " + updatedCount + " field(s) updated", "success");
            DBState.FieldEditorListId = null;
            DBFieldEditor.FieldEditorGrid = null;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldEditor.SaveFieldEditor");
        }
    };

    // -----------------------------------------------------------------------
    // Private: grid construction
    // -----------------------------------------------------------------------

    private static BuildGrid = (list: IListDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("fieldEditorGridContainer");
            if (container === null) { return; }

            DBFieldEditor.FieldEditorGrid = new kEditableTable();
            DBFieldEditor.FieldEditorGrid.AddBlankRows = false;

            // Locked reference columns
            const colInternal = DBFieldEditor.FieldEditorGrid.AddColumn("Internal Name", kHTMLEditableColType.Text, 150, "InternalName", "");
            colInternal.Locked = true;
            const colType = DBFieldEditor.FieldEditorGrid.AddColumn("Type", kHTMLEditableColType.Text, 110, "TypeLabel", "");
            colType.Locked = true;

            // Editable columns
            DBFieldEditor.FieldEditorGrid.AddColumn("Display Name", kHTMLEditableColType.Text, 200, "Title", "");
            const colRequired = DBFieldEditor.FieldEditorGrid.AddColumn("Required", kHTMLEditableColType.Checkbox, 80, "Required", false);
            colRequired;  // suppress unused warning
            DBFieldEditor.FieldEditorGrid.AddColumn("Max Length", kHTMLEditableColType.Number, 100, "MaxLength", 0);
            DBFieldEditor.FieldEditorGrid.AddColumn("Default Value", kHTMLEditableColType.Text, 150, "DefaultValue", "");

            // Build data collection
            const collection: FieldRowCollection = new FieldRowCollection();
            const entries: [string, IFieldDefinition][] = Object.entries(list.Properties || {}) as [string, IFieldDefinition][];
            for (const [key, field] of entries) {
                const row: FieldRow = new FieldRow();
                row.InternalName = key;
                row.Title = field.Title || key;
                row.TypeLabel = DBFieldEditor.GetTypeLabel(field);
                row.Required = field.Required === true;
                row.MaxLength = field.MaxLength || 0;
                row.DefaultValue = field.DefaultValue || "";
                collection.push(row);
            }

            DBFieldEditor.FieldEditorGrid.RowValues = collection;
            container.innerHTML = "";
            container.appendChild(DBFieldEditor.FieldEditorGrid.ToTable());
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldEditor.BuildGrid");
        }
    };

    private static GetTypeLabel = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let key: string;
            if (field.Items !== null && field.Items !== undefined && field.Items.Enum) {
                key = "multi-choice";
            } else if (field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) {
                key = "choice";
            } else if (field.Format) {
                key = field.Format;
            } else if (field.Type === "string") {
                key = "text";
            } else {
                key = field.Type || "text";
            }
            return FIELD_TYPES[key] ? FIELD_TYPES[key].Label : key;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldEditor.GetTypeLabel");
            return "";
        }
    };
}
